/**
 * History date normalisation, as the previous dashboard's DataLoader did
 * (ERT-Web game/js/data-loader.js normalizeHistoryDates): every data item's
 * newest history point becomes "now" and the others step back by the median
 * interval between the shipped points, so demo data never looks stale. The
 * detected interval is kept per item for the simulator's cadence.
 */
import type { DataItem, Element, Subsystem } from '../types';

export const DEFAULT_HISTORY_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000;

const intervals = new WeakMap<DataItem, number>();

/** ISO 8601 or the shipped "YYYY/MM/DD HH:MM" (read as UTC); NaN when unparseable. */
export function parseHistoryDate(text: string): number {
  const m = /^(\d{4})\/(\d{2})\/(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(text.trim());
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0));
  return Date.parse(text);
}

/** Median of the positive gaps between consecutive times (any order); null when there are none. */
export function medianIntervalMs(timesMs: number[]): number | null {
  const sorted = timesMs.filter((t) => Number.isFinite(t)).sort((a, b) => b - a);
  const gaps: number[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const gap = sorted[i] - sorted[i + 1];
    if (gap > 0) gaps.push(gap);
  }
  if (!gaps.length) return null;
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)];
}

/** Rewrites one item's history datetimes in place (newest = now) and records its interval. */
export function normaliseItemHistory(item: DataItem, nowMs: number): void {
  const history = item.history;
  if (!history || history.length === 0) {
    intervals.set(item, DEFAULT_HISTORY_INTERVAL_MS);
    return;
  }
  if (history.length === 1) {
    history[0].datetime = new Date(nowMs).toISOString();
    intervals.set(item, DEFAULT_HISTORY_INTERVAL_MS);
    return;
  }
  const parsed = history.map((point, index) => ({ point, time: parseHistoryDate(point.datetime), index }));
  const median = medianIntervalMs(parsed.map((p) => p.time));
  if (median === null) {
    intervals.set(item, DEFAULT_HISTORY_INTERVAL_MS);
    return;
  }
  intervals.set(item, median);
  // Newest first; unparseable dates sort last and keep their relative order.
  parsed.sort((a, b) => {
    const ta = Number.isFinite(a.time) ? a.time : -Infinity;
    const tb = Number.isFinite(b.time) ? b.time : -Infinity;
    return tb - ta || a.index - b.index;
  });
  parsed.forEach((p, rank) => {
    p.point.datetime = new Date(nowMs - rank * median).toISOString();
  });
}

/** Normalises every data item of every subsystem (recursively) and returns the same element. */
export function normaliseHistoryDates(element: Element, nowMs: number = Date.now()): Element {
  const walk = (subsystems: Subsystem[] | undefined) => {
    for (const s of subsystems ?? []) {
      for (const item of s.data ?? []) normaliseItemHistory(item, nowMs);
      walk(s.childs);
    }
  };
  walk(element.subsystems);
  return element;
}

/** Interval detected for an item at normalisation time (30 days when unknown). */
export function historyIntervalMs(item: DataItem): number {
  return intervals.get(item) ?? DEFAULT_HISTORY_INTERVAL_MS;
}
